import {
  Fragment,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

// import ReactDOM from "react-dom";
import invariant from "tiny-invariant";

import FocusRing from "@atlaskit/focus-ring";
import ChevronDownIcon from "@atlaskit/icon/glyph/chevron-down";
import ChevronRightIcon from "@atlaskit/icon/glyph/chevron-right";
import { ModalTransition } from "@atlaskit/modal-dialog";
import {
  type Instruction,
  type ItemMode,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { pointerOutsideOfPreview } from "@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import type { DragLocationHistory } from "@atlaskit/pragmatic-drag-and-drop/types";

import { type TreeItem as TreeItemType } from "../../data/tree";

// import { indentPerLevel } from "./constants";
// import { MoveDialog } from "./move-dialog";
import { DependencyContext, TreeContext } from "../tree-context";
import {
  alpha,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { createRoot } from "react-dom/client";
import useStyles from "./styles";
import {
  ControlPointDuplicateRounded,
  DeleteRounded,
  EditRounded,
  InputRounded,
  IosShareRounded,
  MoreVert,
  RestoreFromTrashRounded,
  RestoreRounded,
  WebRounded,
} from "@mui/icons-material";
import { RenameModal } from "../renameModal";
import { Edge } from "@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/dist/types/types";
import api from "../../../../../services/api";
import { useNotification } from "../../../../notificationProvider";
import { Socket } from "socket.io-client";
import { DuplicateItemModal } from "../duplicateItemModal ";

function GroupIcon({ isOpen }: { isOpen: boolean }) {
  const Icon = isOpen ? ChevronDownIcon : ChevronRightIcon;
  const color = useTheme().palette.primary.main;
  return <Icon label="" primaryColor={color} />;
}

function Icon({ item }: { item: TreeItemType }) {
  if (!item.children.length) {
    // return <ChildIcon />;
    return;
  }
  return <GroupIcon isOpen={item.isOpen ?? false} />;
}

function Preview({ item, theme }: { item: TreeItemType; theme: Theme }) {
  const styles = useStyles(theme);
  return (
    <Box sx={styles.previewStyles}>
      {" "}
      {item.type === "FILHO" ? (
        <WebRounded sx={styles.icon} fontSize="large" />
      ) : null}
      {item.title}
    </Box>
  );
}

function getParentLevelOfInstruction(instruction: Instruction): number {
  if (instruction.type === "instruction-blocked") {
    return getParentLevelOfInstruction(instruction.desired);
  }
  if (instruction.type === "reparent") {
    return instruction.desiredLevel - 1;
  }
  return instruction.currentLevel - 1;
}

function delay({
  waitMs: timeMs,
  fn,
}: {
  waitMs: number;
  fn: () => void;
}): () => void {
  let timeoutId: number | null = window.setTimeout(() => {
    timeoutId = null;
    fn();
  }, timeMs);
  return function cancel() {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
}

const IconPage = memo(function TreeItem({
  item,
  mode,
  level,
  index,
  socket,
  setExpanded,
  handleUpdateStatus,
}: {
  item: TreeItemType;
  mode: ItemMode;
  level: number;
  index: number;
  socket: Socket;
  setExpanded: (value: "paginas-livres" | "lixeira" | false) => void;
  handleUpdateStatus: (params: { itemId: string; status: number }) => void;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const indentPerLevel = 32;
  const [state, setState] = useState<
    "idle" | "dragging" | "preview" | "parent-of-instruction"
  >("idle");
  const [instruction, setInstruction] = useState<Instruction | null>(null);
  const cancelExpandRef = useRef<(() => void) | null>(null);

  const { dispatch, uniqueContextId, getPathToItem, registerTreeItem } =
    useContext(TreeContext);
  const { DropIndicatorPage, attachInstruction, extractInstruction } =
    useContext(DependencyContext);
  const toggleOpen = useCallback(() => {
    dispatch({ type: "toggle", itemId: item.id });
  }, [dispatch, item]);
  const styles = useStyles(theme);
  const actionMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const notify = useNotification();

  // useEffect(() => {
  //   invariant(buttonRef.current);
  //   invariant(actionMenuTriggerRef.current);
  //   return registerTreeItem({
  //     itemId: item.id,
  //     element: buttonRef.current,
  //     actionMenuTrigger: actionMenuTriggerRef.current,
  //   });
  // }, [item.id, registerTreeItem]);

  const cancelExpand = useCallback(() => {
    cancelExpandRef.current?.();
    cancelExpandRef.current = null;
  }, []);

  const clearParentOfInstructionState = useCallback(() => {
    setState((current) =>
      current === "parent-of-instruction" ? "idle" : current
    );
  }, []);

  // When an item has an instruction applied
  // we are highlighting it's parent item for improved clarity
  const shouldHighlightParent = useCallback(
    (location: DragLocationHistory): boolean => {
      const target = location.current.dropTargets[0];

      if (!target) {
        return false;
      }

      const instruction = extractInstruction(target.data);

      if (!instruction) {
        return false;
      }

      const targetId = target.data.id;
      invariant(typeof targetId === "string");

      const path = getPathToItem(targetId);
      const parentLevel: number = getParentLevelOfInstruction(instruction);
      const parentId = path[parentLevel];
      return parentId === item.id;
    },
    [getPathToItem, extractInstruction, item]
  );

  useEffect(() => {
    invariant(buttonRef.current);

    function updateIsParentOfInstruction({
      location,
    }: {
      location: DragLocationHistory;
    }) {
      if (shouldHighlightParent(location)) {
        setState("parent-of-instruction");
        return;
      }
      clearParentOfInstructionState();
    }

    return combine(
      draggable({
        element: buttonRef.current,
        getInitialData: () => ({
          id: item.id,
          type: "tree-item",
          isOpenOnDragStart: item.isOpen,
          uniqueContextId,
        }),
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            getOffset: pointerOutsideOfPreview({ x: "16px", y: "8px" }),
            render: ({ container }) => {
              const root = createRoot(container);
              root.render(<Preview item={item} theme={theme} />);
              // ReactDOM.render(<Preview item={item} />, container);
              // return () => ReactDOM.unmountComponentAtNode(container);
              return () => root.unmount();
            },
            nativeSetDragImage,
          });
        },
        onDragStart: ({ source }) => {
          setState("dragging");
          // collapse open items during a drag
          if (source.data.isOpenOnDragStart) {
            dispatch({ type: "collapse", itemId: item.id });
          }
        },
        onDrop: ({ source }) => {
          setState("idle");
          if (source.data.isOpenOnDragStart) {
            dispatch({ type: "expand", itemId: item.id });
          }
        },
      }),
      dropTargetForElements({
        element: buttonRef.current,
        getData: ({ input, element }) => {
          const data = { id: item.id };

          return attachInstruction(data, {
            input,
            element,
            indentPerLevel,
            currentLevel: level,
            mode,
            block: item.type === "FILHO" ? ["make-child"] : [],
          });
        },
        canDrop: ({ source }) =>
          source.data.type === "tree-item" &&
          source.data.uniqueContextId === uniqueContextId,
        getIsSticky: () => true,
        onDrag: ({ self, source }) => {
          const instruction = extractInstruction(self.data);

          if (source.data.id !== item.id) {
            // expand after 500ms if still merging
            if (
              instruction?.type === "make-child" &&
              item.children.length &&
              !item.isOpen &&
              !cancelExpandRef.current
            ) {
              cancelExpandRef.current = delay({
                waitMs: 500,
                fn: () => dispatch({ type: "expand", itemId: item.id }),
              });
            }
            if (instruction?.type !== "make-child" && cancelExpandRef.current) {
              cancelExpand();
            }

            setInstruction(instruction);
            return;
          }
          if (instruction?.type === "reparent") {
            setInstruction(instruction);
            return;
          }
          setInstruction(null);
        },
        onDragStart: ({ self, location }) => {
          const rect = self.element.getBoundingClientRect();
          const middleX = rect.left + rect.width / 2;

          // Verifica se o cursor está mais próximo da esquerda ou da direita
          const input = location.current.input;
          if (input.clientX < middleX) {
            setClosestEdge("left");
          } else {
            setClosestEdge("right");
          }
        },
        onDragEnter: ({ self, location }) => {
          const rect = self.element.getBoundingClientRect();
          const middleX = rect.left + rect.width / 2;
          const input = location.current.input;
          // Verifica se o cursor está mais próximo da esquerda ou da direita
          if (input.clientX < middleX) {
            setClosestEdge("left");
          } else {
            setClosestEdge("right");
          }
        },
        onDragLeave: () => {
          cancelExpand();
          setInstruction(null);
          setClosestEdge(null);
        },
        onDrop: () => {
          cancelExpand();
          setInstruction(null);
          setClosestEdge(null);
        },
      }),
      // dropTargetForElements({
      //   element: buttonRef.current,
      //   getData: ({ input, element }) => {
      //     const data = { id: item.id };

      //     return attachInstruction(data, {
      //       input,
      //       element,
      //       indentPerLevel,
      //       currentLevel: level,
      //       mode,
      //       block: item.type === "FILHO" ? ["make-child"] : [],
      //     });
      //   },
      //   canDrop: ({ source }) =>
      //     source.data.type === "tree-item" &&
      //     source.data.uniqueContextId === uniqueContextId,
      //   getIsSticky: () => true,
      //   onDrag: ({ self, source }) => {
      //     const instruction = extractInstruction(self.data);

      //     if (source.data.id !== item.id) {
      //       // expand after 500ms if still merging
      //       if (
      //         instruction?.type === "make-child" &&
      //         item.children.length &&
      //         !item.isOpen &&
      //         !cancelExpandRef.current
      //       ) {
      //         cancelExpandRef.current = delay({
      //           waitMs: 500,
      //           fn: () => dispatch({ type: "expand", itemId: item.id }),
      //         });
      //       }
      //       if (instruction?.type !== "make-child" && cancelExpandRef.current) {
      //         cancelExpand();
      //       }

      //       setInstruction(instruction);
      //       return;
      //     }
      //     if (instruction?.type === "reparent") {
      //       setInstruction(instruction);
      //       return;
      //     }
      //     setInstruction(null);
      //   },
      //   onDragLeave: () => {
      //     cancelExpand();
      //     setInstruction(null);
      //   },
      //   onDrop: () => {
      //     cancelExpand();
      //     setInstruction(null);
      //   },
      // }),
      monitorForElements({
        canMonitor: ({ source }) =>
          source.data.uniqueContextId === uniqueContextId,
        onDragStart: updateIsParentOfInstruction,
        onDrag: updateIsParentOfInstruction,
        onDrop() {
          clearParentOfInstructionState();
        },
      })
    );
  }, [
    dispatch,
    item,
    mode,
    level,
    cancelExpand,
    uniqueContextId,
    extractInstruction,
    attachInstruction,
    getPathToItem,
    clearParentOfInstructionState,
    shouldHighlightParent,
  ]);

  useEffect(
    function mount() {
      return function unmount() {
        cancelExpand();
      };
    },
    [cancelExpand]
  );

  const aria = (() => {
    if (!item.children.length) {
      return undefined;
    }
    return {
      "aria-expanded": item.isOpen,
      "aria-controls": `tree-item-${item.id}--subtree`,
    };
  })();

  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isNewItemDialogOpen, setIsNewItemDialogOpen] = useState(false);
  const [isDuplicateItemDialogOpen, setIsDuplicateItemDialogOpen] =
    useState(false);
  const [loadingExport, setLoadingExport] = useState(false);

  const openRenameDialog = useCallback(() => {
    handleClose();
    setIsRenameDialogOpen(true);
  }, []);
  const closeRenameDialog = useCallback(() => {
    setIsRenameDialogOpen(false);
  }, []);
  const openDuplicateItemModal = useCallback(() => {
    handleClose();
    setIsDuplicateItemDialogOpen(true);
  }, []);
  const closeDuplicateItemModal = useCallback(() => {
    setIsDuplicateItemDialogOpen(false);
  }, []);
  // const openNewItemDialog = useCallback(() => {
  //   setIsNewItemDialogOpen(true);
  // }, []);
  // const closeNewItemDialog = useCallback(() => {
  //   setIsNewItemDialogOpen(false);
  // }, []);
  const handleInsertItemTreeView = useCallback(() => {
    handleUpdateStatus({
      itemId: item.id as string,
      status: 1,
    });
    handleClose();
  }, [dispatch, item]);
  async function handleExportPageApi(): Promise<any | null | undefined> {
    try {
      setLoadingExport(true);
      const response = await api.post(
        "/pages/export",
        {
          id: item.id,
        }
        // { signal }
      );
      if (response.status === 200) {
        // notify("Página exportada com sucesso", "success");
        // console.log("Exported data:", response.data);
        return response.data;
      }
    } catch (error: any) {
      if (error.code && !error.response) {
        notify(error.code, error.message, "error");
      } else {
        notify(error.status, error?.response?.data.message, "error");
      }
      console.error(error);
    } finally {
      setLoadingExport(false);
    }
    return null;
  }
  const handleButtonExport = useCallback(async () => {
    handleClose();
    const data = await handleExportPageApi();
    if (!data) {
      notify(null, "Erro ao exportar página", "error");
      return;
    }
    try {
      // Converte o objeto em uma string JSON
      const jsonString = JSON.stringify(data, null, 2);

      // Cria um blob com o conteúdo JSON
      const blob = new Blob([jsonString], { type: "application/json" });

      // Cria um link para o blob
      const url = URL.createObjectURL(blob);

      // Cria um elemento <a> para o download
      const link = document.createElement("a");
      link.href = url;
      link.download = "data.json"; // Nome do arquivo

      // Simula o clique no link para iniciar o download
      link.click();

      // Libera o objeto URL após o download
      URL.revokeObjectURL(url);
      notify(null, "Página exportada com sucesso", "success");
    } catch (error) {
      notify(null, "Erro ao exportar página", "error");
    }
  }, [handleExportPageApi, notify]);
  async function handleDuplicatePageApi(
    id: string
  ): Promise<any | null | undefined> {
    try {
      setLoadingExport(true);
      const response = await api.post(
        "/pages/duplicate",
        {
          id,
          status: 2,
        }
        // { signal }
      );
      if (response.status === 200) {
        notify(response.status, "Página duplicada com sucesso", "success");
        // console.log("Duplicate page data:", response.data);
        return response.data;
      }
    } catch (error: any) {
      if (error.code && !error.response) {
        notify(error.code, error.message, "error");
      } else {
        notify(error.status, error?.response?.data.message, "error");
      }
      console.error(error);
    } finally {
      setLoadingExport(false);
    }
    return null;
  }

  const handleButtonSubmitDuplicate = useCallback(async () => {
    const data = await handleDuplicatePageApi(item.id);
    if (!data || !data.newPage) {
      notify(null, "Erro ao duplicar página", "error");
      return;
    }
    const newItem = data?.newPage;
    const parentId = data?.parent?.id || null;
    if (data.parent)
      dispatch({
        type: "create-child",
        itemId: newItem.id,
        parentId,
        newItem: {
          id: newItem.id,
          title: newItem.title,
          path: newItem.path,
          type: newItem.type,
          status: newItem.status,
          position: newItem.position,
          children: [],
        },
      });
    else
      dispatch({
        type: "create",
        itemId: newItem.id,
        newItem: {
          id: newItem.id,
          title: newItem.title,
          path: newItem.path,
          type: newItem.type,
          status: newItem.status,
          position: newItem.position,
          children: [],
        },
      });
    handleClose();
  }, [item.id]);
  async function handleApiRestoreDumpPage(
    id: string,
    status: number
  ): Promise<any | null | undefined> {
    try {
      // setLoadingDump(true);
      const response = await api.put("/pages/updateStatus", {
        id,
        status,
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (error: any) {
      notify(null, "Erro ao exportar página", "error");
      console.error(error);
    } finally {
      // setLoadingExport(false);
    }
    return null;
  }
  const handleButtonRestore = useCallback(async () => {
    const data = await handleApiRestoreDumpPage(item.id, 2);
    if (!data) {
      notify(null, "Erro ao restaurar página", "error");
      return;
    }
    dispatch({
      type: "status",
      itemId: item.id,
      status: 2, // Restaurar página
    });
    setExpanded("paginas-livres");
  }, [item.id]);
  const handleButtonDump = useCallback(async () => {
    handleUpdateStatus({
      itemId: item.id,
      status: 0,
    });
    handleClose();
  }, [item.id]);
  const lastUpdated = item.editadoEm
    ? new Date(new Date(item.editadoEm).setHours(0, 0, 0, 0)).getTime()
    : 0;
  const now = Date.now();
  const interval = 7 - Math.floor((now - lastUpdated) / (60 * 60 * 24 * 1000));
  return (
    <Fragment>
      <Box
        sx={[
          styles.outerStyles,
          state === "idle" ? styles.outerHoverStyles : null,
        ]}
      >
        <FocusRing isInset>
          <button
            {...aria}
            style={{
              ...styles.outerButtonStyles(item),
              // paddingLeft: level * indentPerLevel,
              ...(instruction &&
              (instruction.type === "make-child" ||
                instruction.type === "reparent")
                ? { border: `1px solid ${theme.palette.primary.main}` }
                : undefined),
            }}
            id={`tree-item-${item.id}`}
            onClick={toggleOpen}
            ref={buttonRef}
            type="button"
            data-index={index}
            data-level={level}
            data-testid={`tree-item-${item.id}`}
          >
            {/* {closestEdge ? (
              <DropIndicatorPage edge={closestEdge} item={item} />
            ) : null} */}
            <span
              style={{
                ...styles.innerButtonStyles,
                ...(state === "dragging"
                  ? styles.innerDraggingStyles
                  : state === "parent-of-instruction"
                  ? styles.parentOfInstructionStyles
                  : {}),
              }}
            >
              {/* <Icon item={item} /> */}
              {item.type === "FILHO" ? (
                <WebRounded sx={styles.icon} fontSize="large" />
              ) : null}
              <Tooltip title={item.title} placement="top">
                <Typography sx={styles.labelStyles(item)}>
                  {item.title}
                </Typography>
              </Tooltip>
              {item.status === 0 ? (
                <Typography sx={styles.labelExpiredDays}>
                  {interval + " dias"}
                </Typography>
              ) : null}
              {item.status === 0 ? (
                <Tooltip title="Restaurar Página">
                  <IconButton
                    sx={styles.iconOption}
                    size="small"
                    onClick={handleButtonRestore}
                  >
                    <RestoreFromTrashRounded />
                  </IconButton>
                </Tooltip>
              ) : (
                <IconButton
                  sx={styles.iconOption}
                  size="small"
                  onClick={handleClick}
                >
                  <MoreVert />
                </IconButton>
              )}
              {/* <small style={styles.idStyles}>
                {item.type === "FILHO" ? <code>Draft</code> : null}
                <code style={styles.debugStyles}>({mode})</code>
              </small> */}
            </span>
            {/* {instruction ? (
              <DropIndicatorIcon instruction={instruction} position={"below"} />
            ) : null} */}
            {/* <DropIndicatorPage type="terminal" edge={"right"} /> */}
          </button>
        </FocusRing>
      </Box>

      <ModalTransition>
        {/* <MoveModal
          onClose={closeMoveDialog}
          itemId={item.id}
          open={isMoveDialogOpen}
        /> */}
        <RenameModal
          onClose={closeRenameDialog}
          itemId={item.id}
          item={item}
          open={isRenameDialogOpen}
        />
        <DuplicateItemModal
          onClose={closeDuplicateItemModal}
          itemId={item.id}
          item={item}
          open={isDuplicateItemDialogOpen}
        />
      </ModalTransition>
      <Menu
        id={`tree-item-${item.id}--menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: theme.palette.background.card,
            boxShadow: "0px 4px 14px 0px  #0000002c",
          },
        }}
      >
        <MenuItem onClick={handleButtonExport}>
          <ListItemIcon>
            <IosShareRounded fontSize="small" />
          </ListItemIcon>
          Exportar Página
        </MenuItem>
        <MenuItem onClick={openDuplicateItemModal}>
          <ListItemIcon>
            <ControlPointDuplicateRounded fontSize="small" />
          </ListItemIcon>
          Duplicar
        </MenuItem>
        <MenuItem onClick={handleInsertItemTreeView}>
          <ListItemIcon>
            <InputRounded fontSize="small" />
          </ListItemIcon>
          Adicionar à Barra de Navegação
        </MenuItem>
        <MenuItem onClick={openRenameDialog}>
          <ListItemIcon>
            <EditRounded fontSize="small" />
          </ListItemIcon>
          Renomear
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleButtonDump}>
          <ListItemIcon>
            <DeleteRounded color="error" fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.error.main }}>
            Mover para a Lixeira
          </ListItemText>
        </MenuItem>
      </Menu>
    </Fragment>
  );
});

export default IconPage;
